/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/materialAnalysisPropertyConfig.ts.
 */

export type MaterialPropertyKey =
  | "density"
  | "compressiveStrength"
  | "diameter"
  | "uValue"
  | "tensileStrength"
  | "temperature"
  | "coverage"
  | "thickness";

/** Type definition — Material Property Config. */
export interface MaterialPropertyConfig {
  propertyKey: MaterialPropertyKey;
  label: string;
  unitSymbol: string;
  unitDisplay: string;
}

/** Sentinel for no material property filter (field is optional). */
export const NO_MATERIAL_PROPERTY_VALUE = "";

/** Default when user opts in without picking a specific property first. */
export const DEFAULT_MATERIAL_PROPERTY_KEY: MaterialPropertyKey = "density";

/** Static catalog — not filtered by submission data. */
export const MATERIAL_ANALYSIS_PROPERTIES: MaterialPropertyConfig[] = [
  {
    propertyKey: "density",
    label: "Density",
    unitSymbol: "kg/m3",
    unitDisplay: "kg/m³",
  },
  {
    propertyKey: "compressiveStrength",
    label: "Compressive Strength",
    unitSymbol: "MPa",
    unitDisplay: "MPa",
  },
  {
    propertyKey: "diameter",
    label: "Diameter",
    unitSymbol: "mm",
    unitDisplay: "mm",
  },
  {
    propertyKey: "uValue",
    label: "U-value",
    unitSymbol: "W/m2.K",
    unitDisplay: "W/m²·K",
  },
  {
    propertyKey: "tensileStrength",
    label: "Tensile Strength",
    unitSymbol: "MPa",
    unitDisplay: "MPa",
  },
  {
    propertyKey: "temperature",
    label: "Temperature",
    unitSymbol: "deg C",
    unitDisplay: "°C",
  },
  {
    propertyKey: "coverage",
    label: "Coverage",
    unitSymbol: "m2",
    unitDisplay: "m²",
  },
  {
    propertyKey: "thickness",
    label: "Thickness",
    unitSymbol: "mm",
    unitDisplay: "mm",
  },
];

/** Exported function — get Material Property By Key. */
export function getMaterialPropertyByKey(
  propertyKey: string | null | undefined
): MaterialPropertyConfig | null {
  if (!propertyKey) return null;
  return (
    MATERIAL_ANALYSIS_PROPERTIES.find((entry) => entry.propertyKey === propertyKey) ??
    null
  );
}

/** Exported function — format Material Property Range Label. */
export function formatMaterialPropertyRangeLabel(
  config: MaterialPropertyConfig | null,
  min: string,
  max: string
): string {
  if (!config) return "-";
  const minValue = min.trim();
  const maxValue = max.trim();
  if (!minValue && !maxValue) return config.label;
  if (minValue && maxValue) {
    return `${config.label}: ${minValue} – ${maxValue} ${config.unitDisplay}`;
  }
  if (minValue) return `${config.label}: ≥ ${minValue} ${config.unitDisplay}`;
  return `${config.label}: ≤ ${maxValue} ${config.unitDisplay}`;
}
